import type { KeyboardEvent, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'

export type SegmentedOption<T extends string> = {
    value: T
    label: string
    count?: number
    icon?: LucideIcon
}

interface SegmentedControlProps<T extends string> {
    label: string
    value: T
    options: SegmentedOption<T>[]
    onChange: (value: T) => void
    size?: 'sm' | 'default'
    stretch?: boolean
    className?: string
}

const SegmentedControl = <T extends string>({
    label,
    value,
    options,
    onChange,
    size = 'default',
    stretch = false,
    className
}: SegmentedControlProps<T>): ReactNode => {
    const refs = useRef<(HTMLButtonElement | null)[]>([])

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
        const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End']

        if (!keys.includes(event.key)) return

        event.preventDefault()

        let next = index

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % options.length
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + options.length) % options.length
        if (event.key === 'Home') next = 0
        if (event.key === 'End') next = options.length - 1

        onChange(options[next].value)
        refs.current[next]?.focus()
    }

    return (
        <div
            role='radiogroup'
            aria-label={label}
            className={cn(
                'inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-lg bg-muted p-1 no-scrollbar',
                stretch && 'flex w-full',
                className
            )}
        >
            {options.map((option, index) => {
                const selected = option.value === value
                const Icon = option.icon

                return (
                    <button
                        key={option.value}
                        ref={element => {
                            refs.current[index] = element
                        }}
                        type='button'
                        role='radio'
                        aria-checked={selected}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => onChange(option.value)}
                        onKeyDown={event => handleKeyDown(event, index)}
                        className={cn(
                            'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
                            size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-8 px-3 text-sm',
                            stretch && 'flex-1',
                            selected
                                ? 'bg-background text-foreground shadow-xs ring-1 ring-foreground/5'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {Icon && <Icon className='size-4' aria-hidden='true' />}
                        {option.label}

                        {option.count !== undefined && (
                            <span
                                className={cn(
                                    'min-w-5 rounded-full px-1.5 text-[0.7rem] leading-5 tabular-nums',
                                    selected ? 'bg-muted text-foreground' : 'bg-background/70 text-muted-foreground'
                                )}
                            >
                                {option.count}
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )
}

export default SegmentedControl
